import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "dropdown", "filterChip", "chipBadge", "chipIcon", "chipText", "dismissButton"]
  static values = {
    url: String
  }

  connect() {
    this.timeout = null
    this.minChars = 2
    this.activePrefix = null
    this.prefixes = {
      'fund:': { label: 'fund:', icon: 'fund', color: 'blue', length: 5 },
      'company:': { label: 'company:', icon: 'company', color: 'purple', length: 8 },
      'investor:': { label: 'investor:', icon: 'investor', color: 'green', length: 9 }
    }
  }

  disconnect() {
    this.clearTimeout()
  }

  search() {
    this.clearTimeout()

    const query = this.inputTarget.value.trim()

    // Check for filter prefix
    this.updateFilterChip(query)

    if (query.length < this.minChars) {
      this.hideDropdown()
      return
    }

    // Debounce the search
    this.timeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  updateFilterChip(query) {
    const lowerQuery = query.toLowerCase()
    let activePrefix = null

    for (const [prefix, config] of Object.entries(this.prefixes)) {
      if (lowerQuery.startsWith(prefix)) {
        activePrefix = config
        break
      }
    }

    if (activePrefix && this.hasFilterChipTarget) {
      // Store the active prefix
      this.activePrefix = activePrefix

      // Show chip
      this.filterChipTarget.classList.remove('hidden')

      // Update chip text
      if (this.hasChipTextTarget) {
        this.chipTextTarget.textContent = activePrefix.label
      }

      // Update chip icon
      if (this.hasChipIconTarget) {
        this.chipIconTarget.innerHTML = this.getIconSvg(activePrefix.icon)
      }

      // Update chip color
      this.updateChipColor(activePrefix.color)

      // Find the actual prefix string
      const prefixString = Object.keys(this.prefixes).find(p => lowerQuery.startsWith(p))

      // Use negative text-indent to hide the prefix text
      // Calculate based on actual string length with a more accurate multiplier
      if (prefixString) {
        const prefixLength = prefixString.length
        this.inputTarget.style.textIndent = `-${prefixLength * 8.5}px` // Adjusted character width
      }

      // Adjust padding based on chip width (give it a moment to render)
      setTimeout(() => {
        const chipWidth = this.filterChipTarget.offsetWidth
        this.inputTarget.style.paddingLeft = `${chipWidth + 48}px` // 48px = base padding + spacing
      }, 0)
    } else if (this.hasFilterChipTarget) {
      this.activePrefix = null
      this.filterChipTarget.classList.add('hidden')
      this.inputTarget.style.paddingLeft = '2.5rem' // Reset to default (pl-10)
      this.inputTarget.style.textIndent = '0' // Reset text indent
    }
  }

  updateChipColor(color) {
    const colorClasses = {
      blue: {
        bg: 'bg-blue-100', text: 'text-blue-800', darkBg: 'dark:bg-blue-900', darkText: 'dark:text-blue-300',
        btnText: 'text-blue-400', btnHoverBg: 'hover:bg-blue-200', btnHoverText: 'hover:text-blue-900',
        btnDarkHoverBg: 'dark:hover:bg-blue-800', btnDarkHoverText: 'dark:hover:text-blue-300'
      },
      purple: {
        bg: 'bg-purple-100', text: 'text-purple-800', darkBg: 'dark:bg-purple-900', darkText: 'dark:text-purple-300',
        btnText: 'text-purple-400', btnHoverBg: 'hover:bg-purple-200', btnHoverText: 'hover:text-purple-900',
        btnDarkHoverBg: 'dark:hover:bg-purple-800', btnDarkHoverText: 'dark:hover:text-purple-300'
      },
      green: {
        bg: 'bg-green-100', text: 'text-green-800', darkBg: 'dark:bg-green-900', darkText: 'dark:text-green-300',
        btnText: 'text-green-400', btnHoverBg: 'hover:bg-green-200', btnHoverText: 'hover:text-green-900',
        btnDarkHoverBg: 'dark:hover:bg-green-800', btnDarkHoverText: 'dark:hover:text-green-300'
      }
    }

    // Remove all color classes from chip badge
    Object.values(colorClasses).forEach(classes => {
      if (this.hasChipBadgeTarget) {
        this.chipBadgeTarget.classList.remove(classes.bg, classes.text, classes.darkBg, classes.darkText)
      }
      if (this.hasDismissButtonTarget) {
        this.dismissButtonTarget.classList.remove(
          classes.btnText, classes.btnHoverBg, classes.btnHoverText,
          classes.btnDarkHoverBg, classes.btnDarkHoverText
        )
      }
    })

    // Add new color classes to chip badge
    const newClasses = colorClasses[color]
    if (this.hasChipBadgeTarget) {
      this.chipBadgeTarget.classList.add(newClasses.bg, newClasses.text, newClasses.darkBg, newClasses.darkText)
    }

    // Add new color classes to dismiss button
    if (this.hasDismissButtonTarget) {
      this.dismissButtonTarget.classList.add(
        newClasses.btnText, newClasses.btnHoverBg, newClasses.btnHoverText,
        newClasses.btnDarkHoverBg, newClasses.btnDarkHoverText
      )
    }
  }

  getIconSvg(type) {
    const icons = {
      fund: '<path fill="currentColor" d="M4.41085 14.5258L7.81249 11.1241L10.6409 13.9526L13.7978 10.7957L12.0049 9.00281H17.0049V14.0028L15.212 12.2099L10.6409 16.781L7.81249 13.9526L5.33834 16.4267C6.77158 18.5822 9.22233 20.0028 12.0049 20.0028C16.4232 20.0028 20.0049 16.4211 20.0049 12.0028C20.0049 7.58453 16.4232 4.00281 12.0049 4.00281C7.5866 4.00281 4.00488 7.58453 4.00488 12.0028C4.00488 12.8844 4.14747 13.7326 4.41085 14.5258ZM2.87288 16.084L2.86275 16.0739L2.86662 16.07C2.31276 14.8274 2.00488 13.4511 2.00488 12.0028C2.00488 6.47996 6.48204 2.00281 12.0049 2.00281C17.5277 2.00281 22.0049 6.47996 22.0049 12.0028C22.0049 17.5257 17.5277 22.0028 12.0049 22.0028C7.93574 22.0028 4.43426 19.5724 2.87288 16.084Z"></path>',
      company: '<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>',
      investor: '<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>'
    }
    return icons[type] || ''
  }

  async performSearch(query) {
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('q', query)

      const response = await fetch(url, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        const html = await response.text()
        this.resultsTarget.innerHTML = html
        this.showDropdown()
      }
    } catch (error) {
      console.error('Search error:', error)
    }
  }

  showDropdown() {
    this.dropdownTarget.classList.remove('hidden')
  }

  hideDropdown() {
    this.dropdownTarget.classList.add('hidden')
  }

  selectResult() {
    this.hideDropdown()
    this.inputTarget.value = ''
    if (this.hasFilterChipTarget) {
      this.filterChipTarget.classList.add('hidden')
      this.inputTarget.style.paddingLeft = '2.5rem' // Reset to default
      this.inputTarget.style.textIndent = '0' // Reset text indent
    }
    this.activePrefix = null
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  removeFilter(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.activePrefix) return

    // Find the active prefix string
    const prefixString = Object.keys(this.prefixes).find(p =>
      this.prefixes[p] === this.activePrefix
    )

    if (prefixString) {
      // Remove the prefix from the input value
      const currentValue = this.inputTarget.value
      if (currentValue.toLowerCase().startsWith(prefixString)) {
        this.inputTarget.value = currentValue.substring(prefixString.length)
      }
    }

    // Hide the chip and reset styles
    this.filterChipTarget.classList.add('hidden')
    this.inputTarget.style.paddingLeft = '2.5rem'
    this.inputTarget.style.textIndent = '0'
    this.activePrefix = null

    // Focus back on input
    this.inputTarget.focus()

    // Trigger search with updated query
    this.search()
  }

  clearTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
    }
  }

  handleEscape(event) {
    if (event.key === 'Escape') {
      this.hideDropdown()
      this.inputTarget.blur()
    }
  }
}
